import type { ParsedData, Resume, ResumeRequest } from '../types';

type CanonicalEducation = {
  institution: string;
  degree: string;
  fieldOfStudy?: string;
  startDate?: string;
  endDate?: string;
  year?: string;
  gpa?: string;
};

type CanonicalExperience = {
  company: string;
  role: string;
  startDate?: string;
  endDate?: string;
  currentJob: boolean;
  description?: string;
};

type CanonicalResume = {
  templateId: string;
  personalInfo: {
    fullName: string;
    email: string;
    phone?: string;
    address?: string;
    linkedIn?: string;
    website?: string;
    photo?: string;
  };
  summary?: string;
  experience: CanonicalExperience[];
  education: CanonicalEducation[];
  skills: string[];
};

function normalizeEducation(edu: ResumeRequest['education'][number]): CanonicalEducation {
  return {
    institution: edu.institution || '',
    degree: edu.degree || '',
    fieldOfStudy: edu.fieldOfStudy || '',
    startDate: edu.startDate || '',
    endDate: edu.endDate || '',
    year: edu.year || '',
    gpa: edu.gpa || '',
  };
}

function normalizeExperience(exp: ResumeRequest['experience'][number]): CanonicalExperience {
  const asServer = exp as unknown as { currentJob?: boolean; current?: boolean };
  return {
    company: exp.company || '',
    role: exp.role || '',
    startDate: exp.startDate || '',
    endDate: exp.endDate || '',
    currentJob: asServer.currentJob ?? exp.current ?? false,
    description: exp.description || '',
  };
}

function normalizeCanonical(data: ResumeRequest): CanonicalResume {
  return {
    templateId: data.templateId,
    personalInfo: {
      fullName: data.personalInfo?.fullName || '',
      email: data.personalInfo?.email || '',
      phone: data.personalInfo?.phone || '',
      address: data.personalInfo?.address || '',
      linkedIn: data.personalInfo?.linkedin || (data.personalInfo as unknown as { linkedIn?: string })?.linkedIn || '',
      website: data.personalInfo?.website || '',
      photo: data.personalInfo?.photo || '',
    },
    summary: data.summary || '',
    experience: (data.experience || [])
      .filter((exp) => (exp.company || '').trim() && (exp.role || '').trim())
      .map(normalizeExperience),
    education: (data.education || [])
      .filter((edu) => (edu.institution || '').trim() && (edu.degree || '').trim())
      .map(normalizeEducation),
    skills: data.skills || [],
  };
}

export function toResumeRequest(resume: Resume): ResumeRequest {
  const asServer = resume as unknown as {
    personalInfo?: { linkedIn?: string; linkedin?: string };
    experience?: Array<{ currentJob?: boolean; current?: boolean }>;
  };

  return {
    templateId: resume.templateId,
    personalInfo: {
      fullName: resume.personalInfo?.fullName || '',
      email: resume.personalInfo?.email || '',
      phone: resume.personalInfo?.phone || '',
      address: resume.personalInfo?.address || '',
      linkedin: asServer.personalInfo?.linkedIn || resume.personalInfo?.linkedin || '',
      website: resume.personalInfo?.website || '',
      photo: (resume.personalInfo as unknown as { photo?: string })?.photo || '',
    },
    summary: resume.summary || '',
    experience: (resume.experience || []).map((exp, i) => ({
      company: exp.company || '',
      role: exp.role || '',
      startDate: exp.startDate || '',
      endDate: exp.endDate || '',
      current: asServer.experience?.[i]?.currentJob ?? exp.current ?? false,
      description: exp.description || '',
    })),
    education: (resume.education || []).map((edu) => ({
      institution: edu.institution || '',
      degree: edu.degree || '',
      fieldOfStudy: edu.fieldOfStudy || '',
      year: edu.year || edu.endDate || edu.startDate || '',
      gpa: edu.gpa || '',
      startDate: edu.startDate || '',
      endDate: edu.endDate || '',
      current: false,
    })),
    skills: resume.skills || [],
  };
}

export function convertParsedToResumeRequest(parsed: ParsedData, templateId: string): ResumeRequest {
  return {
    templateId,
    personalInfo: {
      fullName: parsed.personalInfo?.fullName || '',
      email: parsed.personalInfo?.email || '',
      phone: parsed.personalInfo?.phone || '',
      address: parsed.personalInfo?.address || '',
      linkedin: parsed.personalInfo?.linkedIn || '',
      website: parsed.personalInfo?.website || '',
    },
    summary: parsed.summary || '',
    experience: parsed.experience?.map(exp => ({
      company: exp.company || '',
      role: exp.role || '',
      startDate: exp.startDate || '',
      endDate: exp.endDate || '',
      current: exp.currentJob || false,
      description: exp.description || '',
    })) || [],
    education: parsed.education?.map(edu => ({
      institution: edu.institution || '',
      degree: edu.degree || '',
      fieldOfStudy: edu.fieldOfStudy || '',
      gpa: edu.gpa || '',
      startDate: edu.startDate || '',
      endDate: edu.endDate || (edu.year || ''),
      current: false,
      year: edu.year || edu.endDate || edu.startDate || '',
    })) || [],
    skills: parsed.skills || [],
  };
}

export function toParsedData(w: ResumeRequest): ParsedData {
  return {
    personalInfo: {
      fullName: w.personalInfo.fullName,
      email: w.personalInfo.email,
      phone: w.personalInfo.phone,
      address: w.personalInfo.address,
      linkedIn: w.personalInfo.linkedin,
      website: w.personalInfo.website,
    },
    summary: w.summary,
    experience: w.experience.map(exp => ({
      company: exp.company,
      role: exp.role,
      startDate: exp.startDate,
      endDate: exp.endDate,
      description: exp.description,
      currentJob: exp.current,
    })),
    education: w.education.map(edu => ({
      institution: edu.institution,
      degree: edu.degree,
      fieldOfStudy: edu.fieldOfStudy,
      startDate: edu.startDate,
      endDate: edu.endDate,
      gpa: edu.gpa,
      year: edu.year || edu.endDate || edu.startDate,
    })),
    skills: w.skills,
  };
}

export function isSameResume(working: ResumeRequest, saved: Resume): boolean {
  return JSON.stringify(normalizeCanonical(working)) === JSON.stringify(normalizeCanonical(toResumeRequest(saved)));
}

export function resumeToParsedData(resume: Resume): ParsedData {
  const asServer = resume as unknown as {
    personalInfo?: { linkedIn?: string; linkedin?: string };
    experience?: Array<{ currentJob?: boolean; current?: boolean }>;
  };

  return {
    personalInfo: {
      fullName: resume.personalInfo?.fullName || '',
      email: resume.personalInfo?.email || '',
      phone: resume.personalInfo?.phone || '',
      address: resume.personalInfo?.address || '',
      linkedIn: asServer.personalInfo?.linkedIn || resume.personalInfo?.linkedin || '',
      website: resume.personalInfo?.website || '',
    },
    summary: resume.summary || '',
    experience: (resume.experience || []).map((exp, i) => ({
      company: exp.company || '',
      role: exp.role || '',
      startDate: exp.startDate || '',
      endDate: exp.endDate || '',
      description: exp.description || '',
      currentJob: asServer.experience?.[i]?.currentJob ?? exp.current ?? false,
    })),
    education: (resume.education || []).map((edu) => ({
      institution: edu.institution || '',
      degree: edu.degree || '',
      fieldOfStudy: edu.fieldOfStudy || '',
      startDate: edu.startDate || '',
      endDate: edu.endDate || '',
      year: edu.year || edu.endDate || edu.startDate || '',
      gpa: edu.gpa || '',
    })),
    skills: resume.skills || [],
  };
}
